import { ref, computed } from 'vue';
import { getHomelandListApi } from '@api/homeland';

// Global state, avoid duplicate requests
const homelandList = ref([]);
const loading = ref(false);
const error = ref(null);
let isInitialized = false;

export default function useHomelandList() {
  const getHomelandList = async (forceRefresh = false) => {
    // If it has been initialized and is not a forced refresh, return the cached data directly
    if (isInitialized && !forceRefresh) {
      return homelandList.value;
    }

    // If it is loading, wait for the current request to complete
    if (loading.value) {
      return new Promise(resolve => {
        const checkLoading = () => {
          if (!loading.value) {
            resolve(homelandList.value);
          } else {
            setTimeout(checkLoading, 50);
          }
        };
        checkLoading();
      });
    }

    try {
      loading.value = true;
      error.value = null;
      const res = await getHomelandListApi();
      homelandList.value = res || [];
      isInitialized = true;
      return homelandList.value;
    } catch (err) {
      error.value = err;
      console.error('Failed to fetch homeland list:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  // Get all homeland options (for dropdown selection)
  const homelandOptions = computed(() => {
    return homelandList.value.map(item => ({
      label: item.homeland_name,
      value: `${item.homeland}`
    }));
  });

  // Get all homeland options (for dropdown selection, filtering out the current homeland)
  const getFilteredHomelandOptions = currentHomeland => {
    return homelandList.value
      .filter(item => item.homeland !== currentHomeland)
      .map(item => ({
        label: item.homeland_name,
        value: item.homeland
      }));
  };

  // Get homeland options for only home_owner role
  const ownerHomelandOptions = computed(() => {
    return homelandList.value
      .filter(item => item.role === 'home_owner' || item.role === 'admin')
      .map(item => ({
        label: item.homeland_name,
        value: item.homeland
      }));
  });

  const ownerAndManagerOptions = computed(() => {
    return homelandList.value
      .filter(item => item.role !== 'home_member')
      .map(item => ({
        label: item.homeland_name,
        value: item.homeland
      }));
  });

  // Get homeland options for only home_owner role (filtering out the current homeland)
  const getFilteredOwnerHomelandOptions = currentHomeland => {
    return homelandList.value
      .filter(
        item =>
          (item.role === 'home_owner' || item.role === 'admin') &&
          item.homeland !== currentHomeland
      )
      .map(item => ({
        label: item.homeland_name,
        value: item.homeland
      }));
  };

  // Get homeland information based on homeland ID
  const getHomelandById = homelandId => {
    return homelandList.value.find(item => item.homeland === homelandId);
  };

  // Refresh data
  const refresh = () => {
    isInitialized = false;
    return getHomelandList(true);
  };

  return {
    homelandList: computed(() => homelandList.value),
    homelandOptions,
    ownerHomelandOptions,
    ownerAndManagerOptions,
    loading: computed(() => loading.value),
    error: computed(() => error.value),
    getHomelandList,
    getFilteredHomelandOptions,
    getFilteredOwnerHomelandOptions,
    getHomelandById,
    refresh
  };
}
